import React from 'react'
import PaymentRow from '../PaymentRow'
import { PAYMENT_DISPLAY_TYPES, type AggregatedData, type TotalData } from '../types'
import { usePriceDetailsHelpers } from '../hooks'

interface PaymentDisplayWithTotalDataProps {
  totalData: TotalData
  totalCurrency: string
  payNowLabel: string
  depositSuffix: string
  formattedPayDate: string | undefined
}

function PaymentDisplayWithTotalData({
  totalData,
  totalCurrency,
  payNowLabel,
  depositSuffix,
  formattedPayDate,
}: Readonly<PaymentDisplayWithTotalDataProps>) {
  const { formatMessageCapitalized } = usePriceDetailsHelpers()

  const hasPayLater = !!totalData.payLate
  const shouldShowZeroPayNow = hasPayLater && !totalData.payNow

  return (
    <>
      {shouldShowZeroPayNow && (
        <PaymentRow label={payNowLabel} amount={0} currency={totalCurrency} />
      )}
      {!!totalData.payNow && (
        <PaymentRow
          label={`${payNowLabel} ${depositSuffix}`}
          amount={Math.round(totalData.payNow)}
          currency={totalCurrency}
        />
      )}
      {!!totalData.payLate && (
        <PaymentRow
          label={formatMessageCapitalized({ id: 'policies.pay-on' }, { date: formattedPayDate })}
          amount={Math.round(totalData.payLate)}
          currency={totalCurrency}
        />
      )}
    </>
  )
}
PaymentDisplayWithTotalData.displayName = 'PaymentDisplayWithTotalData'

interface PayOnArrivalPaymentProps {
  totalCurrency: string
  payNowLabel: string
  totalPrice: number
  formattedPayDate: string | undefined
}

function PayOnArrivalPayment({
  totalCurrency,
  payNowLabel,
  totalPrice,
  formattedPayDate,
}: Readonly<PayOnArrivalPaymentProps>) {
  const { formatMessageCapitalized } = usePriceDetailsHelpers()

  return (
    <>
      <PaymentRow label={payNowLabel} amount={0} currency={totalCurrency} />
      <PaymentRow
        label={formatMessageCapitalized({ id: 'policies.pay-on' }, { date: formattedPayDate })}
        amount={Math.round(totalPrice)}
        currency={totalCurrency}
      />
    </>
  )
}
PayOnArrivalPayment.displayName = 'PayOnArrivalPayment'

interface DepositPaymentProps {
  totalCurrency: string
  payNowLabel: string
  depositSuffix: string
  payNowWithInsurance: number
  payLate: number
  formattedPayDate: string | undefined
}

function DepositPayment({
  totalCurrency,
  payNowLabel,
  depositSuffix,
  payNowWithInsurance,
  payLate,
  formattedPayDate,
}: Readonly<DepositPaymentProps>) {
  const { formatMessageCapitalized } = usePriceDetailsHelpers()

  const payLateAmount = payLate ? Math.round(payLate) : undefined

  return (
    <>
      <PaymentRow
        label={`${payNowLabel} ${depositSuffix}`}
        amount={Math.round(payNowWithInsurance)}
        currency={totalCurrency}
      />
      <PaymentRow
        label={formatMessageCapitalized({ id: 'policies.pay-on' }, { date: formattedPayDate })}
        amount={payLateAmount}
        currency={totalCurrency}
      />
    </>
  )
}
DepositPayment.displayName = 'DepositPayment'

interface PaymentsByDateProps {
  paymentsByDate: AggregatedData['paymentsByDate']
}

function PaymentsByDate({ paymentsByDate }: Readonly<PaymentsByDateProps>) {
  const { formatMessageCapitalized, formatDate } = usePriceDetailsHelpers()

  if (!paymentsByDate || paymentsByDate.length === 0) {
    return null
  }

  return (
    <>
      {paymentsByDate.map((payment, index) => (
        <PaymentRow
          key={payment.date || `payment-${index}`}
          label={formatMessageCapitalized(
            { id: 'policies.pay-on' },
            { date: formatDate(payment.date) }
          )}
          amount={payment.amount}
          currency={payment.currency}
        />
      ))}
    </>
  )
}
PaymentsByDate.displayName = 'PaymentsByDate'

interface DefaultPaymentProps {
  totalCurrency: string
  payNowLabel: string
  payNowWithInsurance: number
  paymentsByDate: AggregatedData['paymentsByDate']
}

function DefaultPayment({
  totalCurrency,
  payNowLabel,
  payNowWithInsurance,
  paymentsByDate,
}: Readonly<DefaultPaymentProps>) {
  return (
    <>
      {payNowWithInsurance > 0 && (
        <PaymentRow label={payNowLabel} amount={payNowWithInsurance} currency={totalCurrency} />
      )}
      <PaymentsByDate paymentsByDate={paymentsByDate} />
    </>
  )
}
DefaultPayment.displayName = 'DefaultPayment'

interface PaymentDisplayProps {
  totalData: TotalData | undefined
  aggregatedData: AggregatedData
  totalPrice: number
  depositSuffix: string
}

export default function PaymentDisplay({
  totalData,
  aggregatedData,
  totalPrice,
  depositSuffix,
}: Readonly<PaymentDisplayProps>) {
  const { formatMessage, formatDate } = usePriceDetailsHelpers()

  const { totalCurrency, payDate, payNowWithInsurance, payLate, paymentDisplayType } =
    aggregatedData

  const payNowLabel = formatMessage({ id: 'global.pay-now' })
  const formattedPayDate = payDate ? formatDate(payDate) : undefined

  if (totalData) {
    return (
      <PaymentDisplayWithTotalData
        totalData={totalData}
        totalCurrency={totalCurrency}
        payNowLabel={payNowLabel}
        depositSuffix={depositSuffix}
        formattedPayDate={formattedPayDate}
      />
    )
  }

  if (
    paymentDisplayType === PAYMENT_DISPLAY_TYPES.NON_REFUNDABLE ||
    paymentDisplayType === PAYMENT_DISPLAY_TYPES.PAY_UPON_BOOKING_WITH_LATER_PAYMENT
  ) {
    if (payNowWithInsurance <= 0) {
      return null
    }
    return (
      <PaymentRow
        label={payNowLabel}
        amount={Math.round(payNowWithInsurance)}
        currency={totalCurrency}
      />
    )
  }

  if (paymentDisplayType === PAYMENT_DISPLAY_TYPES.PAY_ON_ARRIVAL_ONLY) {
    return (
      <PayOnArrivalPayment
        totalCurrency={totalCurrency}
        payNowLabel={payNowLabel}
        totalPrice={totalPrice}
        formattedPayDate={formattedPayDate}
      />
    )
  }

  if (paymentDisplayType === PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT) {
    return (
      <DepositPayment
        totalCurrency={totalCurrency}
        payNowLabel={payNowLabel}
        depositSuffix={depositSuffix}
        payNowWithInsurance={payNowWithInsurance}
        payLate={payLate}
        formattedPayDate={formattedPayDate}
      />
    )
  }

  return (
    <DefaultPayment
      totalCurrency={totalCurrency}
      payNowLabel={payNowLabel}
      payNowWithInsurance={payNowWithInsurance}
      paymentsByDate={aggregatedData.paymentsByDate}
    />
  )
}
PaymentDisplay.displayName = 'PaymentDisplay'
